
import { enhancedTargetNotesService } from "../../services/enhancedTargetNotesService";
import { targetService } from "../../services/targetService";
import { chatApi } from "../../services/chatApiService";
import { imageStorageService } from "../../services/imageStorageService";
import { getGoogleMapsApiKey } from "../../services/googleMapsService";

/**
 * Parses Google Maps URL to extract coordinates, panorama ID, and viewing parameters
 */
const parseMapsUrlDetails = (url: string) => {
  // Extract panorama ID from URL
  const panoMatch = url.match(/!1s([^!]+)/);
  const pano = panoMatch ? panoMatch[1] : null;
  
  // Extract coordinates
  const coordMatch = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  let lat = null, lng = null;
  if (coordMatch) {
    lat = coordMatch[1];
    lng = coordMatch[2];
  }
  
  // Extract viewing parameters
  const headingMatch = url.match(/(\d+\.?\d*)h/);
  const pitchMatch = url.match(/(\d+\.?\d*)t/);
  const fovMatch = url.match(/(\d+\.?\d*)y/);
  
  return {
    lat,
    lng,
    pano,
    heading: headingMatch ? parseFloat(headingMatch[1]) : undefined,
    pitch: pitchMatch ? parseFloat(pitchMatch[1]) : undefined,
    fov: fovMatch ? parseFloat(fovMatch[1]) : undefined
  };
};

export const useEnhancedFeedbackHandler = () => {
  const handleTargetAnalysisRequest = async (
    message: string,
    currentTargetCode: string | null,
    sessionId: string | null = null
  ): Promise<string> => {
    if (!currentTargetCode) {
      console.log('📝 EnhancedFeedbackHandler: No current target for analysis');
      return "No target to analyze against. Please generate a target first.";
    }

    if (!sessionId) {
      console.log('📝 EnhancedFeedbackHandler: No sessionId for analysis call');
      return "Analysis requires an active session. Please try again.";
    }

    try {
      console.log('🎯 EnhancedFeedbackHandler: Starting target analysis for:', currentTargetCode);

      // CRITICAL FIX: Wait a moment for any pending background uploads to complete
      console.log('⏳ EnhancedFeedbackHandler: Waiting for background uploads to complete...');
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay

      // Get all notes and the revealed target
      const notes = await enhancedTargetNotesService.getNotesByTargetCode(currentTargetCode);
      const targetDetails = await targetService.getTargetForFeedback(currentTargetCode);

      if (!targetDetails) {
        return "Target details could not be found for analysis.";
      }

      console.log('📝 EnhancedFeedbackHandler: Found notes:', notes.length);
      console.log('🎯 EnhancedFeedbackHandler: Target details:', targetDetails.target_url);

      // Format notes for analysis with clean separation
      const { textContent: notesTextContent, imageUrls, imageMetadata } = enhancedTargetNotesService.formatNotesForAnalysis(notes);
      const imageNotes = notes.filter(n => n.note_type === 'image');

      // CRITICAL FIX: Also fetch user messages from the messages table
      let chatImpressions = '';
      try {
        const { supabase } = await import('../../integrations/supabase/client');
        const { data: messages, error } = await supabase
          .from('messages')
          .select('content')
          .eq('session_id', sessionId)
          .eq('role', 'user')
          .order('created_at', { ascending: true });

        if (!error && messages && messages.length > 0) {
          // Filter out system messages and extract user impressions
          const userImpressions = messages
            .map(m => m.content)
            .filter(content => 
              content && 
              !content.includes('[INTERNAL_ANALYSIS]') &&
              !content.includes('[VISION_ANALYSIS_REQUEST]') &&
              !content.toLowerCase().includes('reveal target') &&
              !content.toLowerCase().includes('generate') &&
              !content.toLowerCase().includes('prediction target') &&
              !content.toLowerCase().includes('location target') &&
              !content.toLowerCase().includes('image target')
            );
          
          chatImpressions = userImpressions.join('\n\n');
          console.log('📝 EnhancedFeedbackHandler: Found chat impressions:', chatImpressions.length, 'chars from', userImpressions.length, 'messages');
        }
      } catch (err) {
        console.error('❌ EnhancedFeedbackHandler: Error fetching messages:', err);
      }

      // Combine notes text and chat impressions
      const allTextImpressions = [notesTextContent, chatImpressions].filter(Boolean).join('\n\n');

      // Allow analysis even without user impressions - still valuable to show target details

      // Create analysis prompt
      let analysisPrompt = `[INTERNAL_ANALYSIS] Analyze remote viewing session for Target ${currentTargetCode}.

TARGET: ${targetDetails.target_url}
TYPE: ${targetDetails.target_type}

USER'S IMPRESSIONS:
${allTextImpressions || "No text impressions provided."}`;

      // Prepare image data array for vision analysis
      const imageDataArray = [];

      // CRITICAL FIX: Add the target image for comparison (location, image, or prediction targets)
      if (targetDetails.target_url && targetDetails.target_type === 'location') {
        console.log('🖼️ EnhancedFeedbackHandler: Processing target Street View for analysis');
        
        try {
          // Parse Google Maps URL for panorama ID and coordinates
          const urlDetails = parseMapsUrlDetails(targetDetails.target_url);
          
          if (!urlDetails.lat || !urlDetails.lng) {
            console.error('❌ EnhancedFeedbackHandler: Could not parse coordinates from target URL:', targetDetails.target_url);
            throw new Error('Could not extract coordinates from target URL');
          }

          console.log('🗺️ EnhancedFeedbackHandler: Parsed URL details:', urlDetails);
          
          // Get the Google Maps API key
          const apiKey = await getGoogleMapsApiKey();
          if (apiKey) {
            let streetViewUrl = '';
            
            if (urlDetails.pano) {
              // ✅ Panorama ID found: Use it for exact view match
              console.log(`🎯 EnhancedFeedbackHandler: Panorama ID found: ${urlDetails.pano}. Fetching exact panoramic view.`);
              streetViewUrl = `https://maps.googleapis.com/maps/api/streetview?pano=${urlDetails.pano}&size=800x400`;
              
              // Add optional parameters if available
              if (urlDetails.heading !== undefined) streetViewUrl += `&heading=${urlDetails.heading}`;
              if (urlDetails.pitch !== undefined) streetViewUrl += `&pitch=${urlDetails.pitch}`;
              if (urlDetails.fov !== undefined) streetViewUrl += `&fov=${urlDetails.fov}`;
              
              streetViewUrl += `&key=${apiKey}`;
            } else {
              // ⚠️ Fallback: No panorama ID, use coordinates as before
              console.warn(`⚠️ EnhancedFeedbackHandler: No panorama ID found. Using coordinate-based view at ${urlDetails.lat}, ${urlDetails.lng}.`);
              streetViewUrl = `https://maps.googleapis.com/maps/api/streetview?location=${urlDetails.lat},${urlDetails.lng}&size=800x400&fov=90&pitch=0&key=${apiKey}`;
            }
            
            console.log('🖼️ EnhancedFeedbackHandler: Street View API URL:', streetViewUrl);
            
            const response = await fetch(streetViewUrl);
            if (response.ok && response.headers.get('content-type')?.startsWith('image/')) {
              const blob = await response.blob();
              const base64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });
              
              imageDataArray.push({
                index: 0,
                timestamp: 'Target Street View',
                base64Data: base64,
                filename: 'target_street_view.jpg'
              });
              
              console.log('✅ EnhancedFeedbackHandler: Target Street View image successfully added for analysis');
            } else {
              console.warn('⚠️ EnhancedFeedbackHandler: Street View API returned non-image response');
            }
          } else {
            console.warn('⚠️ EnhancedFeedbackHandler: No Google Maps API key available');
          }
        } catch (error) {
          console.error('❌ EnhancedFeedbackHandler: Error fetching target Street View image:', error);
        }
      } else if (targetDetails.target_url && targetDetails.target_type === 'image') {
        console.log('🖼️ EnhancedFeedbackHandler: Processing target image for analysis');
        
        try {
          // Fetch the Picsum image directly
          const response = await fetch(targetDetails.target_url);
          if (response.ok && response.headers.get('content-type')?.startsWith('image/')) {
            const blob = await response.blob();
            const base64 = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.readAsDataURL(blob);
            });
            
            imageDataArray.push({
              index: 0,
              timestamp: 'Target Image',
              base64Data: base64,
              filename: 'target_image.jpg'
            });
            
            console.log('✅ EnhancedFeedbackHandler: Target image successfully added for analysis');
          } else {
            console.warn('⚠️ EnhancedFeedbackHandler: Target image fetch returned non-image response');
          }
        } catch (error) {
          console.error('❌ EnhancedFeedbackHandler: Error fetching target image:', error);
        }
      } else if (targetDetails.target_type === 'prediction') {
        // PREDICTION TARGET: Fetch BOTH yes and no images for proper comparison
        console.log('🔮 EnhancedFeedbackHandler: Processing prediction target images for analysis');
        
        const predictionDetails = targetDetails as { 
          target_url: string; 
          target_type: string;
          yes_image_url?: string;
          no_image_url?: string;
        };
        
        // Fetch YES image (Option A)
        if (predictionDetails.yes_image_url) {
          try {
            console.log('🖼️ EnhancedFeedbackHandler: Fetching YES image:', predictionDetails.yes_image_url);
            const response = await fetch(predictionDetails.yes_image_url);
            if (response.ok && response.headers.get('content-type')?.startsWith('image/')) {
              const blob = await response.blob();
              const base64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });
              
              imageDataArray.push({
                index: 0,
                timestamp: 'Option A (YES)',
                base64Data: base64,
                filename: 'prediction_yes_image.jpg'
              });
              
              console.log('✅ EnhancedFeedbackHandler: YES image added for analysis');
            }
          } catch (error) {
            console.error('❌ EnhancedFeedbackHandler: Error fetching YES image:', error);
          }
        }
        
        // Fetch NO image (Option B)
        if (predictionDetails.no_image_url) {
          try {
            console.log('🖼️ EnhancedFeedbackHandler: Fetching NO image:', predictionDetails.no_image_url);
            const response = await fetch(predictionDetails.no_image_url);
            if (response.ok && response.headers.get('content-type')?.startsWith('image/')) {
              const blob = await response.blob();
              const base64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });
              
              imageDataArray.push({
                index: 0,
                timestamp: 'Option B (NO)',
                base64Data: base64,
                filename: 'prediction_no_image.jpg'
              });
              
              console.log('✅ EnhancedFeedbackHandler: NO image added for analysis');
            }
          } catch (error) {
            console.error('❌ EnhancedFeedbackHandler: Error fetching NO image:', error);
          }
        }
        
        // Update the analysis prompt for prediction targets
        analysisPrompt = `[INTERNAL_ANALYSIS] Analyze remote viewing session for Prediction Target ${currentTargetCode}.

TARGET TYPE: Prediction (Binary Choice)
You are viewing TWO target images - Option A (YES outcome) and Option B (NO outcome).

USER'S IMPRESSIONS:
${allTextImpressions || "No text impressions provided."}

CRITICAL: Compare the user's impressions against BOTH prediction images. 
Determine which image (A or B) their impressions align with more closely, or if they align with neither.`;
      }

      // Add user-submitted images with clear separation
      if (imageNotes.length > 0) {
        console.log('🖼️ EnhancedFeedbackHandler: Processing user image impressions:', imageNotes.length);
        
        analysisPrompt += `\n\n📷 USER'S SUBMITTED IMAGES:\nThe user submitted ${imageNotes.length} image${imageNotes.length > 1 ? 's' : ''} as visual impressions:`;
        
        // Use the clean metadata from formatNotesForAnalysis
        imageMetadata.forEach((meta, index) => {
          const filename = meta.filename.startsWith('Image: ') ? meta.filename.substring(7) : 'user_sketch.jpg';
          analysisPrompt += `\nImage ${index + 1}: Submitted at ${meta.timestamp} (${filename})`;
        });
        
        analysisPrompt += `\n\nCRITICAL: You will receive ${imageNotes.length} user-submitted image${imageNotes.length > 1 ? 's' : ''} for vision analysis. These are visual impressions (sketches, drawings, diagrams) created by the user. Analyze ONLY what you can see in these images visually - ignore any text descriptions. Compare the visual content to the target image/location.`;
        
        for (let i = 0; i < imageNotes.length; i++) {
          const imageNote = imageNotes[i];
          const timestamp = new Date(imageNote.submitted_at).toLocaleTimeString();
          
          if (imageNote.file_path) {
            try {
              const publicUrl = imageStorageService.getPublicUrl(imageNote.file_path);
              console.log('🖼️ EnhancedFeedbackHandler: Processing user image:', {
                filePath: imageNote.file_path,
                publicUrl,
                noteContent: imageNote.note_content
              });
              
              const response = await fetch(publicUrl);
              console.log('🖼️ EnhancedFeedbackHandler: Fetch response:', {
                status: response.status,
                statusText: response.statusText,
                contentType: response.headers.get('content-type'),
                ok: response.ok
              });
              
              if (response.ok) {
                const blob = await response.blob();
                console.log('🖼️ EnhancedFeedbackHandler: Blob created:', {
                  size: blob.size,
                  type: blob.type
                });
                
                const base64 = await new Promise<string>((resolve) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result as string);
                  reader.readAsDataURL(blob);
                });
                
                imageDataArray.push({
                  index: i + 1,
                  timestamp,
                  base64Data: base64,
                  filename: imageNote.file_path?.split('/').pop() || `user_image_${i + 1}`
                });
                
                console.log('✅ EnhancedFeedbackHandler: User image successfully added for analysis:', {
                  dataLength: base64.length,
                  filename: imageNote.file_path?.split('/').pop()
                });
              } else {
                console.error('❌ EnhancedFeedbackHandler: Failed to fetch user image:', {
                  status: response.status,
                  statusText: response.statusText,
                  url: publicUrl
                });
              }
            } catch (error) {
              console.error('❌ EnhancedFeedbackHandler: Error fetching user image:', {
                error: error.message,
                filePath: imageNote.file_path,
                stack: error.stack
              });
            }
          }
        }
      }

      // Structure the vision analysis request properly
      if (imageDataArray.length > 0) {
        analysisPrompt += `\n\n[VISION_ANALYSIS_REQUEST]
IMAGE_COUNT: ${imageDataArray.length}
IMAGE_DATA: ${JSON.stringify(imageDataArray)}`;
        
        console.log('🔄 EnhancedFeedbackHandler: Structured vision analysis with', imageDataArray.length, 'images');
        console.log('🔄 EnhancedFeedbackHandler: Target image included:', imageDataArray.some(img => img.index === 0));
      }

      // Special instructions based on whether user submitted impressions
      const hasUserImpressions = allTextImpressions || imageNotes.length > 0;
      
      if (hasUserImpressions) {
        analysisPrompt += `

CRITICAL INSTRUCTION: Use ONLY second person language throughout your entire response. Address the user directly as "you" and "your" in EVERY sentence. Never use "the user", "user's", or third person references.

Examples of CORRECT language:
- "Your target is a busy street scene..."
- "Your sketch shows an arched structure..."
- "You described repetitive beams..."
- "Your impression focused on..."
- "There are no repetitive beams in what you're viewing..."

Examples of INCORRECT language (DO NOT USE):
- "The user's target is..."
- "The user described..."
- "User's Image(s)"
- "The user's impressions..."

Please provide structured feedback comparing your impressions to the actual target:

✅ **Accurate Elements** - What you got right about the target
❌ **Inaccurate Elements** - What didn't match your impressions  
🔄 **Partial Connections** - Elements with some connection to what you described
💡 **Additional Observations** - Key aspects you missed

Keep response concise and encouraging while being specific about matches/mismatches. Remember: EVERY sentence must address the user directly as "you" or "your".

${imageDataArray.length > 0 ? `IMPORTANT: Compare your visual impressions (sketches/drawings) to what is actually visible in the target ${targetDetails.target_type === 'location' ? 'Street View location' : 'image'}. Analyze both the target ${targetDetails.target_type === 'location' ? 'Street View image' : 'image'} AND your submitted images together for accurate comparison.` : ''}`;
      } else {
        analysisPrompt += `

SPECIAL CASE: The user has NOT submitted any impressions before revealing this target.

Please provide a target-only analysis with this structure:
- Target Overview: Detailed description of what the target shows
- User Impressions: Note that no impressions were submitted
- Analysis: Empty arrays for matches/mismatches, but populate "additional_observations" with key elements to look for when comparing written notes or memory to the scene
- Overall Assessment: Encourage sharing impressions before revealing targets next time, and guide them on what to compare if they have written notes or memory

Focus on describing the target details that would be useful for remote viewing practice.`;
      }

      console.log('🔄 EnhancedFeedbackHandler: Sending enhanced analysis request to OpenAI');
      console.log('🔄 EnhancedFeedbackHandler: Total images for analysis:', imageDataArray.length);

      // CRITICAL FIX: Use a special content type to distinguish internal analysis messages
      const internalAnalysisContent = `[INTERNAL_ANALYSIS] ${analysisPrompt}`;
      
      // Submit to OpenAI via the chat edge function
      try {
        await chatApi.sendMessage(sessionId, internalAnalysisContent);
      } catch (apiError: any) {
        console.error('❌ EnhancedFeedbackHandler: API error:', apiError);
        
        // Check for specific error types
        const errorMessage = apiError?.message || apiError?.toString() || '';
        if (errorMessage.toLowerCase().includes('insufficient tokens') || errorMessage.includes('402')) {
          return "⚠️ Insufficient tokens to analyze your impressions. Please purchase more tokens to continue.";
        }
        
        return "An error occurred while analyzing your target impressions. Please try again.";
      }
      
      return "🎯 Target analysis initiated! Your personalized feedback will appear in the chat shortly, comparing your impressions (including visual sketches) to the revealed target location.";
    } catch (error) {
      console.error('❌ EnhancedFeedbackHandler error:', error);
      return "An error occurred while analyzing your target impressions. Please try again.";
    }
  };

  return { handleTargetAnalysisRequest };
};
